import { createAsyncSelectors } from '@lib/selectors';
import { useAction } from '../useAction';
import { useInitiativeReference } from './useInitiativeReference';
import { useOnMount } from '../useOnMount';
import { usePrevious } from '../usePrevious';
import { useSonarSelector } from '../useSonarSelector';
import { coreActions, Task } from '@sonar-software/sonar-core';
import { useCallback, useEffect } from 'react';

export interface UseInitiativeTasksResult {
	isRequested: boolean;
	refresh: () => void;
	tasks: Array<Task>;
}

const selectors = createAsyncSelectors((state) => state.core.initiatives.tasks.get);

export function useInitiativeTasks(initiativeId: number, refresh = false): UseInitiativeTasksResult {
	const isRequested = useSonarSelector(selectors.isRequested);
	const previousInitiativeId = usePrevious(initiativeId);

	const request = useAction(coreActions.requestGetCoreInitiativesTasks);

	const loadTasks = useCallback(() => {
		if (initiativeId) {
			request({ initiativeId });
		}
	}, [ initiativeId, request ]);

	useOnMount(() => {
		if (refresh) {
			loadTasks();
		}
	});

	useEffect(() => {
		if (previousInitiativeId && previousInitiativeId !== initiativeId) {
			loadTasks();
		}
	}, [ initiativeId, loadTasks, previousInitiativeId ]);

	const { tasks } = useInitiativeReference(initiativeId);

	return {
		isRequested,
		refresh: loadTasks,
		tasks
	};
}
